import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/hen-ui/utils';

export const cardVariants = cva('flex flex-col items-start', {
  variants: {
    variant: {
      'background-white': 'bg-white',
      'background-gray': 'bg-neutral-50',
      'background-secondary': 'bg-secondary-50',
      'background-transparent': 'bg-transparent',
      'background-primary-tint': 'bg-surface-primary-subtle',
      'background-accent-tint': 'bg-surface-accent-subtle',
      'background-accent': 'bg-surface-accent',
      'background-chrome': 'bg-surface-chrome',
      'background-primary': 'bg-primary-900',
    },
    // `border` owns the edge treatment (stroke / elevation) only. The
    // padded `padding` / `stroke` / `shadow` values are legacy aliases that
    // still bake in `p-lg` for back-compat; new border tokens are
    // border-only and pair with the orthogonal `padding` axis below. To
    // render a padded-alias border without its padding, set `padding='none'`
    // (it wins via tailwind-merge — see the `padding` axis). The `shadow`
    // axis likewise wins over the legacy `shadow` alias's elevation.
    border: {
      none: '',
      padding: 'p-lg',
      stroke: 'p-lg border border-neutral-200',
      strokeLight: 'border border-neutral-100',
      strokeSecondary: 'border border-secondary-700',
      strokeError: 'border border-error-500',
      strokeSuccess: 'border border-success-500',
      shadow: 'p-lg shadow-md',
    },
    // Spacing axis, independent of `border`. `none` emits `p-0` (not '') so
    // it can override a legacy border alias's baked-in padding via
    // tailwind-merge precedence — `padding` is emitted after `border`.
    padding: {
      none: 'p-0',
      min: 'p-min',
      xxs: 'p-xxs',
      xs: 'p-xs',
      sm: 'p-sm',
      md: 'p-md',
      lg: 'p-lg',
      xl: 'p-xl',
      '2xl': 'p-2xl',
      '3xl': 'p-3xl',
      max: 'p-max',
      section: 'px-section-sides py-section-top-bottom',
    },
    // Resting elevation, emitted after `border` so it overrides the legacy
    // alias. No default: unset must leave `border='shadow'` cards as they are.
    shadow: {
      none: 'shadow-none',
      xs: 'shadow-xs',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
    },
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
    // Opt-in clip to the rounded corners. `overflow-clip`, not `overflow-hidden`:
    // hidden makes the card a scroll container and breaks `sticky` descendants.
    clipContent: {
      true: 'overflow-clip',
      false: '',
    },
    sectionGap: {
      none: '',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
    },
    height: {
      auto: 'h-auto',
      full: 'h-full',
    },
  },
  defaultVariants: {
    variant: 'background-white',
    border: 'none',
    rounded: 'lg',
    clipContent: false,
    sectionGap: 'none',
    height: 'auto',
  },
});

export const cardContentVariants = cva('flex flex-col self-start', {
  variants: {
    height: {
      auto: 'h-auto',
      full: 'h-full',
    },
    contentPadding: {
      default: 'py-lg px-xl',
      none: '',
    },
    stackGap: {
      none: 'gap-0',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
    },
    width: {
      fill: 'w-full',
      hug: 'w-fit max-w-full',
    },
    align: {
      left: ['items-start', '[&_*]:text-left'].join(' '),
      center: ['items-center', '[&_*]:text-center'].join(' '),
    },
    'align-md': {
      left: ['md:items-start', 'md:[&_*]:text-left'].join(' '),
      center: ['md:items-center', 'md:[&_*]:text-center'].join(' '),
    },
  },
  defaultVariants: {
    height: 'auto',
    contentPadding: 'default',
    stackGap: 'lg',
    width: 'fill',
  },
});

export type CardVariantProps = VariantProps<typeof cardVariants>;
export type CardContentVariantProps = VariantProps<typeof cardContentVariants>;

type CardBackgroundVariant = NonNullable<CardVariantProps['variant']>;

/**
 * One-step deeper background on link cards, on hover and while the card link
 * has keyboard focus. Focus on a different nested CTA leaves the card as is.
 */
const cardLinkInteractionClassesByVariant = {
  'background-primary-tint':
    'hover:bg-surface-primary-subtle-hover has-[>[data-slot=card-action]:focus-visible]:bg-surface-primary-subtle-hover',
  'background-accent-tint':
    'hover:bg-surface-accent-subtle-hover has-[>[data-slot=card-action]:focus-visible]:bg-surface-accent-subtle-hover',
  'background-secondary':
    'hover:bg-secondary-100 has-[>[data-slot=card-action]:focus-visible]:bg-secondary-100',
  'background-gray':
    'hover:bg-neutral-100 has-[>[data-slot=card-action]:focus-visible]:bg-neutral-100',
} as const satisfies Partial<Record<CardBackgroundVariant, string>>;

/** Elevation a link card takes on hover and while the card link has keyboard focus. */
export type CardHoverShadow = 'none' | 'sm' | 'md' | 'lg' | 'interactive';

const cardLinkHoverShadowClasses = {
  none: '',
  sm: 'hover:shadow-sm has-[>[data-slot=card-action]:focus-visible]:shadow-sm',
  md: 'hover:shadow-md has-[>[data-slot=card-action]:focus-visible]:shadow-md',
  lg: 'hover:shadow-lg has-[>[data-slot=card-action]:focus-visible]:shadow-lg',
  interactive:
    'hover:shadow-hover has-[>[data-slot=card-action]:focus-visible]:shadow-hover',
} as const satisfies Record<CardHoverShadow, string>;

// One `transition-property` list: `transition-colors` and `transition-shadow`
// would each replace the other.
const CARD_LINK_SHADOW_TRANSITION =
  'transition-[color,background-color,border-color,box-shadow] duration-200 motion-reduce:transition-none';

export function cardLinkShellClasses(
  variant: CardVariantProps['variant'],
  isLinkCard: boolean,
  hoverShadow?: CardHoverShadow | null,
): string {
  if (!isLinkCard) return '';
  const deepen =
    variant != null
      ? cardLinkInteractionClassesByVariant[
          variant as keyof typeof cardLinkInteractionClassesByVariant
        ]
      : undefined;
  // DSL values can be null, misspelled, or unresolved bindings at runtime.
  const lift =
    hoverShadow &&
    Object.prototype.hasOwnProperty.call(
      cardLinkHoverShadowClasses,
      hoverShadow,
    )
      ? cardLinkHoverShadowClasses[hoverShadow]
      : '';
  if (!lift) return cn('transition-colors duration-200', deepen);
  return cn(CARD_LINK_SHADOW_TRANSITION, deepen, lift);
}
